import { useState, useEffect } from "react";
import { Icon } from "../shared/Icons";
import { api } from "../../api";

const presets = [
  { label: "Resend",    host: "SDK",                 port: "SDK", hint: "The fastest & most reliable way. Just enter your Resend API Key." },
  { label: "Gmail",     host: "smtp.gmail.com",     port: "587", hint: "Use Gmail App Password (Google Account → Security → App Passwords)" },
  { label: "Outlook",   host: "smtp-mail.outlook.com", port: "587", hint: "Use your Outlook email and password" },
  { label: "Yahoo",     host: "smtp.mail.yahoo.com", port: "587", hint: "Use Yahoo App Password (Account Security → App Passwords)" },
  { label: "Zoho",      host: "smtp.zoho.com",       port: "587", hint: "Use your Zoho email and password" },
  { label: "Custom SMTP", host: "",                  port: "587", hint: "Enter your own SMTP server details" },
];

export function AdminSettings({ user, showToast }) {
  const [settings, setSettings] = useState({
    app_name: "",
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_pass: "",
    smtp_from_name: "",
    smtp_from_email: "",
    resend_api_key: "",
    admin_ntfy_topic: "",
    admin_ntfy_token: "",
    merchant_upi_id: "",
    merchant_upi_name: "",
  });

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testMsg, setTestMsg]     = useState(null); // { type: 'success'|'error', text }
  const [showPass, setShowPass]   = useState(false);
  const [emailOk, setEmailOk]     = useState(false);
  const [pushTesting, setPushTesting] = useState(false);
  const [pushMsg, setPushMsg]         = useState(null);

  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetHint, setPresetHint]         = useState("");

  useEffect(() => {
    (async () => {
      try {
        const s = await api("/api/settings");
        setSettings(prev => ({ ...prev, ...s }));

        // Infer preset based on keys
        if (s.resend_api_key) {
          setSelectedPreset("Resend");
          setPresetHint(presets[0].hint);
        } else if (s.smtp_host) {
          const matched = presets.find(p => p.host === s.smtp_host);
          if (matched) {
            setSelectedPreset(matched.label);
            setPresetHint(matched.hint);
          } else {
            setSelectedPreset("Custom SMTP");
            setPresetHint(presets[presets.length-1].hint);
          }
        }

        const status = await api("/api/settings/email-status", { auth: false });
        setEmailOk(status.configured);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const upd = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setTestMsg(null);
    try {
      await api("/api/settings", { method: "PUT", body: settings });
      const status = await api("/api/settings/email-status", { auth: false });
      setEmailOk(status.configured);
      showToast("Settings saved successfully");
    } catch (err) {
      showToast("Failed to save settings");
    } finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await api("/api/settings/test-email", { method: "POST" });
      setTestMsg({ type: "success", text: r.message });
    } catch (err) {
      setTestMsg({ type: "error", text: err.message });
    } finally { setTesting(false); }
  };
 
  const handleTestPush = async () => {
    setPushTesting(true); setPushMsg(null);
    try {
      const r = await api("/api/settings/test-push", { method: "POST" });
      setPushMsg({ type: "success", text: r.message });
    } catch (err) {
      setPushMsg({ type: "error", text: err.message });
    } finally { setPushTesting(false); }
  };

  const applyPreset = (p) => {
    setSelectedPreset(p.label);
    setPresetHint(p.hint);
    if (p.label === "Resend") {
      // Clear SMTP fields if switching to Resend
      upd("smtp_host", "");
      upd("smtp_port", "");
      upd("smtp_user", "");
      upd("smtp_pass", "");
    } else {
      // Clear Resend key if switching to SMTP
      upd("resend_api_key", "");
      if (p.host) upd("smtp_host", p.host);
      upd("smtp_port", p.port);
    }
  };


  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:300 }}>
      <div style={{ textAlign:"center", color:"var(--text3)" }}>
        <Icon name="settings" size={32} />
        <div style={{ marginTop:10, fontSize:14 }}>Loading settings…</div>
      </div>
    </div>
  );

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Configure your app and email service</p>
        </div>
      </div>

      {/* ── Email Status Banner ── */}
      <div className={`settings-banner ${emailOk ? "banner-ok" : "banner-warn"}`}>
        <Icon name={emailOk ? "check" : "warning"} size={16} />
        <div>
          <strong>{emailOk ? "Email is configured and ready" : "Email not configured yet"}</strong>
          <div style={{ fontSize:12, opacity:.85, marginTop:2 }}>
            {emailOk
              ? "Customers will receive real verification emails when they sign up."
              : "Customers cannot sign up until you configure SMTP below. Fill in the form and click Save."}
          </div>
        </div>
      </div>

      <div className="settings-grid">

        {/* ── App Info ── */}
        <div className="card settings-card">
          <div className="card-header">
            <span className="card-title">
              <Icon name="tooth" size={15} /> &nbsp;App Information
            </span>
          </div>
          <div className="form-field">
            <label>App / Brand Name</label>
            <input placeholder="K Dental" value={settings.app_name}
              onChange={e => upd("app_name", e.target.value)} />
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>
              Used in email subjects and branding
            </div>
          </div>
        </div>

        {/* ── SMTP Config ── */}
        <div className="card settings-card">
          <div className="card-header">
            <span className="card-title">
              <Icon name="mail" size={15} /> &nbsp;Email Delivery Configuration
            </span>
          </div>


          {/* Provider presets */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:600, color:"var(--text2)", marginBottom:8 }}>
              Quick setup — choose your email provider:
            </div>
            <div className="preset-chips">
              {presets.map(p => (
                <button key={p.label}
                  className={`preset-chip ${selectedPreset === p.label ? "active" : ""} ${p.label === "Resend" ? "recommended" : ""}`}
                  onClick={() => applyPreset(p)}>
                  {p.label === "Resend" && <Icon name="star" size={10} style={{ marginRight:4 }} />}
                  {p.label}
                </button>
              ))}
            </div>
            {presetHint && (
              <div className={`preset-hint ${selectedPreset === "Resend" ? "resend-hint" : ""}`}>
                <Icon name="info" size={12} /> {presetHint}
              </div>
            )}
          </div>


          {selectedPreset === "Resend" ? (
            <div className="form-field highlight-field">
              <label>Resend API Key *</label>
              <div style={{ position:"relative" }}>
                <input type={showPass ? "text" : "password"} placeholder="re_123456789..."
                  value={settings.resend_api_key}
                  onChange={e => upd("resend_api_key", e.target.value)}
                  style={{ paddingRight:38 }} />
                <button type="button" className="eye-btn" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                  <Icon name={showPass ? "eyeOff" : "eye"} size={14} />
                </button>
              </div>
              <div style={{ fontSize:11, color:"var(--accent)", marginTop:4 }}>
                Found in <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer">Resend Dashboard → API Keys</a>
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label>SMTP Host *</label>
                  <input placeholder="smtp.gmail.com" value={settings.smtp_host}
                    onChange={e => upd("smtp_host", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>SMTP Port</label>
                  <input type="number" placeholder="587" value={settings.smtp_port}
                    onChange={e => upd("smtp_port", e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Email Username *</label>
                  <input type="email" placeholder="your@gmail.com" value={settings.smtp_user}
                    onChange={e => upd("smtp_user", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Email Password / App Password *</label>
                  <div style={{ position:"relative" }}>
                    <input type={showPass ? "text" : "password"} placeholder="Your email password"
                      value={settings.smtp_pass}
                      onChange={e => upd("smtp_pass", e.target.value)}
                      style={{ paddingRight:38 }} />
                    <button type="button" className="eye-btn" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                      <Icon name={showPass ? "eyeOff" : "eye"} size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="form-row">
            <div className="form-field">
              <label>From Name (shown in inbox)</label>
              <input placeholder="K Dental" value={settings.smtp_from_name}
                onChange={e => upd("smtp_from_name", e.target.value)} />
            </div>
            <div className="form-field">
              <label>From Email Address</label>
              <input type="email" placeholder="noreply@yourdomain.com" value={settings.smtp_from_email}
                onChange={e => upd("smtp_from_email", e.target.value)} />
              {selectedPreset === "Resend" && (
                <div style={{ fontSize:10, color:"var(--text3)", marginTop:4 }}>
                  Default is <code>onboarding@resend.dev</code> (testing only)
                </div>
              )}
            </div>
          </div>


          {/* Test result */}
          {testMsg && (
            <div className={`test-result ${testMsg.type}`}>
              <Icon name={testMsg.type === "success" ? "check" : "warning"} size={13} />
              {testMsg.text}
            </div>
          )}

          <div className="settings-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><Icon name="check" size={13} /> Save Settings</>}
            </button>
            <button className="btn btn-ghost" onClick={handleTestEmail} disabled={testing || (!settings.smtp_host && !settings.resend_api_key)}>
              {testing ? "Sending…" : <><Icon name="mail" size={13} /> Send Test Email to {user.email}</>}
            </button>

          </div>
        </div>

        {/* ── Mobile Notifications (ntfy.sh) ── */}
        <div className="card settings-card premium-card">
          <div className="card-header">
            <span className="card-title">
              <Icon name="bell" size={15} /> &nbsp;Mobile Notifications (ntfy.sh)
            </span>
          </div>

          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, color:"var(--text2)", lineHeight:1.5, marginBottom:12 }}>
              Get real-time alerts on your phone for every new order. 
              We use <strong>ntfy.sh</strong>, a free and powerful notification service.
            </div>
            
            <div className="ntfy-steps" style={{ background:"rgba(0,0,0,0.03)", padding:12, borderRadius:8, fontSize:12 }}>
              <div style={{ marginBottom:6 }}><strong>1. Install:</strong> Download <code>ntfy</code> app on <a href="https://apps.apple.com/app/ntfy/id1625396347" target="_blank" rel="noreferrer">iOS</a> or <a href="https://play.google.com/store/apps/details?id=io.heckel.ntfy" target="_blank" rel="noreferrer">Android</a>.</div>
              <div style={{ marginBottom:6 }}><strong>2. Subscribe:</strong> Open the app and "Subscribe to topic" using a unique name (e.g., <code>kd_orders_{Math.random().toString(36).slice(-6)}</code>).</div>
              <div style={{ marginBottom:0 }}><strong>3. Connect:</strong> Enter that exact topic name below and Save.</div>
            </div>
          </div>

          <div className="form-field">
            <label>Ntfy Topic Name</label>
            <input placeholder="e.g. kdental_alerts_123" value={settings.admin_ntfy_topic}
              onChange={e => upd("admin_ntfy_topic", e.target.value)} />
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>
              Tip: Use a complex name to keep your notifications private.
            </div>
          </div>

          <div className="form-field">
            <label>Ntfy Access Token (Optional)</label>
            <div style={{ position:"relative" }}>
              <input type={showPass ? "text" : "password"} placeholder="tk_..."
                value={settings.admin_ntfy_token || ""}
                onChange={e => upd("admin_ntfy_token", e.target.value)}
                style={{ paddingRight:38 }} />
              <button type="button" className="eye-btn" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                <Icon name={showPass ? "eyeOff" : "eye"} size={14} />
              </button>
            </div>
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>
              If your ntfy.sh server requires authentication, enter your token here.
            </div>
          </div>

          {pushMsg && (
            <div className={`test-result ${pushMsg.type}`} style={{ marginTop:12 }}>
              <Icon name={pushMsg.type === "success" ? "check" : "warning"} size={13} />
              &nbsp;{pushMsg.text}
            </div>
          )}

          <div className="settings-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><Icon name="check" size={13} /> Save Topic</>}
            </button>
            <button className="btn btn-ghost" onClick={handleTestPush} disabled={pushTesting || !settings.admin_ntfy_topic}>
              {pushTesting ? "Sending…" : <><Icon name="bell" size={13} /> Send Test Push</>}
            </button>
          </div>
        </div>

        {/* ── UPI Payment Configuration ── */}
        <div className="card settings-card">
          <div className="card-header">
            <span className="card-title">
              <Icon name="creditCard" size={15} /> &nbsp;UPI Payment Configuration
            </span>
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, color:"var(--text2)", lineHeight:1.5, marginBottom:12 }}>
              Specify your UPI details to generate <strong>dynamic QR codes</strong> that automatically include the order amount.
            </div>
          </div>
          <div className="form-field">
            <label>Merchant UPI ID (VPA) *</label>
            <input placeholder="e.g. kdentalsupplies@okaxis" value={settings.merchant_upi_id}
              onChange={e => upd("merchant_upi_id", e.target.value)} />
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>
              This is where you will receive payments.
            </div>
          </div>
          <div className="form-field">
            <label>Merchant Name</label>
            <input placeholder="e.g. K-Dental Supplies" value={settings.merchant_upi_name}
              onChange={e => upd("merchant_upi_name", e.target.value)} />
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>
              Customer will see this name in their UPI app.
            </div>
          </div>
          <div className="settings-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><Icon name="check" size={13} /> Save UPI Details</>}
            </button>
          </div>
        </div>

        {/* ── How it works ── */}
        <div className="card settings-card how-it-works">
          <div className="card-header">
            <span className="card-title"><Icon name="info" size={15} /> &nbsp;How Email Verification Works</span>
          </div>
          <div className="how-steps">
            {[
              ["1", "Customer fills in their details on the signup page"],
              ["2", "Server generates a secure 6-digit OTP and sends it via Resend API (or SMTP)"],
              ["3", "Customer enters the OTP — account is verified and they're logged in automatically"],

              ["4", "All passwords are hashed with bcrypt — never stored in plain text"],
              ["5", "JWT tokens handle session auth — tokens expire after 7 days"],
            ].map(([n, text]) => (
              <div className="how-step" key={n}>
                <div className="how-num">{n}</div>
                <div style={{ fontSize:13, color:"var(--text2)" }}>{text}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
